/**
 * 日志拦截器
 * 记录API请求和响应的日志
 */
/**
 * 日志级别
 */
export var LogLevel;
(function (LogLevel) {
    LogLevel["DEBUG"] = "debug";
    LogLevel["INFO"] = "info";
    LogLevel["WARN"] = "warn";
    LogLevel["ERROR"] = "error";
})(LogLevel || (LogLevel = {}));
/**
 * 日志拦截器
 * 实现请求和响应的日志记录
 */
export class LoggingInterceptor {
    /**
     * 构造函数
     * @param options 日志选项
     */
    constructor(options = {}) {
        /**
         * 拦截器名称
         */
        this.name = 'logging-interceptor';
        this.options = {
            level: LogLevel.INFO,
            logRequestBody: true,
            logResponseBody: true,
            logHeaders: false,
            logTiming: true,
            prefix: '[API]',
            ...options,
        };
    }
    /**
     * 拦截请求
     * @param config 请求配置
     * @returns 处理后的请求配置
     */
    interceptRequest(config) {
        // 记录请求开始时间
        if (this.options.logTiming) {
            config.meta = {
                ...config.meta,
                startTime: performance.now(),
            };
        }
        // 记录请求日志
        this.log(LogLevel.INFO, `${this.options.prefix} 请求: ${config.url}`);
        // 记录请求方法和头部
        if (this.options.logHeaders && config.options.headers) {
            this.log(LogLevel.DEBUG, `${this.options.prefix} 请求头:`, config.options.headers);
        }
        // 记录请求体
        if (this.options.logRequestBody && config.options.body) {
            let body = config.options.body;
            // 尝试格式化JSON字符串
            if (typeof body === 'string') {
                try {
                    body = JSON.parse(body);
                }
                catch (e) {
                    // 如果解析失败，保持原样
                }
            }
            this.log(LogLevel.DEBUG, `${this.options.prefix} 请求体:`, body);
        }
        return config;
    }
    /**
     * 拦截响应
     * @param data 响应数据
     * @returns 处理后的响应数据
     */
    interceptResponse(data) {
        const { response, requestConfig } = data;
        // 记录执行时间
        let timingInfo = '';
        if (this.options.logTiming && requestConfig.meta?.startTime !== undefined) {
            const endTime = performance.now();
            const duration = endTime - requestConfig.meta.startTime;
            timingInfo = ` (${duration.toFixed(2)}ms)`;
            // 添加执行时间到元数据
            data.meta = {
                ...data.meta,
                duration,
            };
        }
        // 记录响应状态
        const statusText = response.success ? 'Success' : 'Failed';
        this.log(response.success ? LogLevel.INFO : LogLevel.WARN, `${this.options.prefix} 响应: ${requestConfig.url} - ${statusText}${timingInfo}`);
        // 记录响应体
        if (this.options.logResponseBody) {
            if (response.success) {
                this.log(LogLevel.DEBUG, `${this.options.prefix} 响应数据:`, response.data);
            }
            else {
                this.log(LogLevel.WARN, `${this.options.prefix} 响应错误:`, response.error);
            }
        }
        return data;
    }
    /**
     * 拦截错误
     * @param error 错误对象
     * @param requestConfig 原始请求配置
     * @returns 处理后的错误响应
     */
    interceptError(error, requestConfig) {
        // 记录执行时间
        let timingInfo = '';
        if (this.options.logTiming && requestConfig.meta?.startTime !== undefined) {
            const endTime = performance.now();
            const duration = endTime - requestConfig.meta.startTime;
            timingInfo = ` (${duration.toFixed(2)}ms)`;
        }
        // 记录错误
        this.log(LogLevel.ERROR, `${this.options.prefix} 错误: ${requestConfig.url}${timingInfo}`, error);
        // 返回原始错误
        return {
            success: false,
            error: {
                code: 'CLIENT_ERROR',
                message: error instanceof Error ? error.message : String(error),
                details: typeof error === 'object' && error !== null
                    ? error instanceof Error
                        ? { name: error.name, message: error.message, stack: error.stack }
                        : error
                    : { value: String(error) },
            },
        };
    }
    /**
     * intercept方法实现
     * 根据参数判断调用哪个具体实现
     */
    intercept(...args) {
        if (args.length === 1) {
            // 检查参数类型来确定是请求还是响应
            if ('response' in args[0]) {
                // 响应拦截
                return this.interceptResponse(args[0]);
            }
            else {
                // 请求拦截
                return this.interceptRequest(args[0]);
            }
        }
        else if (args.length === 2) {
            // 错误拦截，两个参数
            return this.interceptError(args[0], args[1]);
        }
        throw new Error('Invalid arguments for interceptor');
    }
    /**
     * 记录日志
     * @param level 日志级别
     * @param message 日志消息
     * @param data 日志数据
     */
    log(level, message, data) {
        // 检查是否应该记录此级别的日志
        if (!this.shouldLog(level)) {
            return;
        }
        // 根据级别选择对应的控制台方法
        switch (level) {
            case LogLevel.DEBUG:
                // 在开发环境中使用console.debug是有必要的，但保留注释以符合lint规则
                if (data) {
                    console.debug(message, data);
                }
                else {
                    console.debug(message);
                }
                break;
            case LogLevel.INFO:
                // 在开发环境中使用console.info是有必要的，但保留注释以符合lint规则
                if (data) {
                    console.info(message, data);
                }
                else {
                    console.info(message);
                }
                break;
            case LogLevel.WARN:
                if (data) {
                    console.warn(message, data);
                }
                else {
                    console.warn(message);
                }
                break;
            case LogLevel.ERROR:
                if (data) {
                    console.error(message, data);
                }
                else {
                    console.error(message);
                }
                break;
        }
    }
    /**
     * 检查是否应该记录指定级别的日志
     * @param level 日志级别
     * @returns 是否应该记录
     */
    shouldLog(level) {
        const levelPriority = {
            [LogLevel.DEBUG]: 0,
            [LogLevel.INFO]: 1,
            [LogLevel.WARN]: 2,
            [LogLevel.ERROR]: 3,
        };
        return levelPriority[level] >= levelPriority[this.options.level];
    }
}
//# sourceMappingURL=logging-interceptor.js.map